// Story 1.18 — /account/wallet/refund page.
//
// Lists the user's refundable top-ups + a modal-confirm flow. Reached
// from WalletPage's "Refund a recent top-up →" link.
//
// The list alone under-reports: an offer hold caps every row to zero, so a
// user bidding their whole balance sees an empty page while the 14-day
// window runs out on money they could still reclaim (offers never expire).
// <HeldFundsNotice> states that money and the way out of it — legal action
// item 12 / lawyer-review item 7 require the refund path to be VISIBLE, and
// an honestly-empty screen is not visible.

import { useState } from "react";
import { Link } from "react-router-dom";

import { HeldFundsNotice } from "../features/refund/HeldFundsNotice";
import { RefundEligibleList } from "../features/refund/RefundEligibleList";
import { RefundModal } from "../features/refund/RefundModal";
import { useHeldOfferFunds } from "../features/refund/useHeldOfferFunds";
import {
  useRefundEligibleList,
  type RefundableTopupItem,
} from "../features/refund/useRefundEligibleList";

export function RefundPage() {
  const [selected, setSelected] = useState<RefundableTopupItem | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const eligible = useRefundEligibleList();
  const held = useHeldOfferFunds();

  const handleSelect = (item: RefundableTopupItem) => {
    setSelected(item);
    setModalOpen(true);
  };

  return (
    <main className="mx-auto flex w-full max-w-2xl flex-col gap-6 p-6 text-brand-paper-100">
      <header className="flex flex-col gap-2">
        <Link
          to="/account/wallet"
          className="text-sm underline decoration-dotted opacity-80"
        >
          ← Back to wallet
        </Link>
        <h1 className="font-heading text-4xl font-bold">Refund a top-up</h1>
        <p className="font-body text-sm opacity-80">
          PSD2 Article 71 lets you refund unspent top-up balance back to
          the original card within 14 days of the top-up date. Pick a
          top-up below to refund.
        </p>
      </header>

      <HeldFundsNotice />

      {eligible.isPending && (
        <p className="font-body text-sm text-muted-foreground">
          Loading refundable top-ups…
        </p>
      )}
      {eligible.isError && (
        <p className="font-body text-sm text-destructive" role="alert">
          Couldn&apos;t load refundable top-ups. Refresh to try again.
        </p>
      )}
      {eligible.data && (
        <RefundEligibleList
          items={eligible.data.items}
          onSelect={handleSelect}
          heldCents={held.heldCents}
        />
      )}

      <RefundModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        item={selected}
      />
    </main>
  );
}
